// Core UI primitive library — built before any screen.
// All primitives live in this file to keep the surface area small and consistent.

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight, Calendar, ClipboardList, Home, Megaphone, MoreHorizontal, Users,
} from "lucide-react";
import { ChalkLogoLockup } from "./Logo";

// ── ChalkCard ──
export function ChalkCard({ children, onClick, className = "", style, ...rest }) {
  return (
    <div
      className={`chalk-card ${onClick ? "cursor-pointer" : ""} ${className}`}
      style={style}
      onClick={onClick}
      {...rest}
    >
      {children}
    </div>
  );
}

// ── DarkHeader ── coach-facing primary screen header
export function DarkHeader({ eyebrow, title, subtitle, right, children }) {
  return (
    <div className="chalk-dark-header">
      <div className="flex items-start justify-between gap-3">
        <div>
          {eyebrow && <div className="chalk-eyebrow">{eyebrow}</div>}
          {title && (
            <div className="font-serif-display" style={{ fontSize: 30, lineHeight: 0.95, marginTop: 6, color: "#F5F0E8" }}>
              {title}
            </div>
          )}
          {subtitle && (
            <div className="chalk-marker" style={{ fontSize: 16, marginTop: 2, color: "#4db87a" }}>
              {subtitle}
            </div>
          )}
        </div>
        {right}
      </div>
      {children && <div style={{ marginTop: 12, position: "relative", zIndex: 1 }}>{children}</div>}
    </div>
  );
}

// ── WhiteHeader ── parent / guardian screen header
export function WhiteHeader({ title, subtitle, avatar }) {
  return (
    <div style={{ background: "#fff", padding: "20px 20px 18px", borderBottom: "0.5px solid var(--chalk-border)" }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-serif-display" style={{ fontSize: 28, color: "var(--chalk-board)" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4, fontFamily: "var(--font-body)" }}>{subtitle}</div>}
        </div>
        {avatar}
      </div>
    </div>
  );
}

// ── SectionTitle ──
export function SectionTitle({ label, className = "" }) {
  return <div className={`chalk-section-title ${className}`}>{label}</div>;
}

// ── EmptyState ──
export function EmptyState({ headline, body, ctaLabel, onCta, icon, testId }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center text-center" style={{ padding: "40px 24px" }}>
      <div
        aria-hidden="true"
        style={{
          width: 120, height: 120, borderRadius: 16, marginBottom: 18,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "rgba(28,43,30,0.30)",
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 16, fontWeight: 500, color: "var(--chalk-board)" }}>{headline}</div>
      {body && <div style={{ fontSize: 14, color: "var(--chalk-muted)", marginTop: 8, maxWidth: 280 }}>{body}</div>}
      {ctaLabel && (
        <button data-testid="empty-state-cta" onClick={onCta} className="chalk-btn-primary" style={{ width: "auto", marginTop: 18 }}>
          {ctaLabel}
          <ArrowUpRight size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

// ── LineupStatTile ──
export function LineupStatTile({ number, label }) {
  return (
    <div style={{ background: "var(--chalk-card-bg, #F2EFE8)", borderRadius: 10, padding: "10px 12px", minWidth: 0 }}>
      <div style={{ fontSize: 17, fontWeight: 500, color: "var(--chalk-board)", lineHeight: 1 }}>{number}</div>
      <div style={{ fontSize: 10, color: "var(--chalk-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>{label}</div>
    </div>
  );
}

// ── ChalkActionButton ──
export function ChalkActionButton({ variant = "primary", label, onClick, loading, icon, disabled, type = "button", ariaLabel, fullWidth = true, testId }) {
  const cls = variant === "ghost" ? "chalk-btn-ghost" : variant === "danger" ? "chalk-btn-danger" : "chalk-btn-primary";
  return (
    <button
      type={type}
      className={cls}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel || label}
      data-testid={testId}
      style={fullWidth ? undefined : { width: "auto" }}
    >
      {loading ? <Spinner /> : (
        <>
          {icon}
          <span>{label}</span>
        </>
      )}
    </button>
  );
}

function Spinner() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.9s" repeatCount="indefinite" />
      </path>
    </svg>
  );
}

// ── UpgradeGateCard ──
// Single source of truth for every Chalkboard upgrade prompt.
// Spec: white card · 0.5px #DAD8D1 border · 14px radius · 2px solid #2E7D52
// left accent · benefit-led copy · "Cancel anytime." line. Fires analytics
// `upgrade_prompt_viewed` on mount and `upgrade_prompt_clicked` on tap.
import { useEffect as _useEffect } from "react";
import { track as _track } from "../../lib/analytics";
import { usePaymentsOpen, EARLY_ACCESS_LABEL } from "../../lib/billingStatus";

export function UpgradeGateCard({
  featureName,
  benefit,
  price = "$11.99/month",
  ctaLabel,
  onUpgrade,
  target = "The Chalkboard",
  context = {},
}) {
  const trackProps = {
    feature_name: featureName,
    source_screen: context.source_screen || null,
    current_tier: context.current_tier || null,
    team_id: context.team_id || null,
  };
  _useEffect(() => {
    _track("upgrade_prompt_viewed", trackProps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureName, context.source_screen]);

  function handleClick() {
    _track("upgrade_prompt_clicked", trackProps);
    if (typeof onUpgrade === "function") onUpgrade();
  }

  const open = usePaymentsOpen();
  const label = open ? (ctaLabel || `Upgrade for ${price} →`) : EARLY_ACCESS_LABEL;

  return (
    <div
      data-testid="upgrade-gate-card"
      data-feature={featureName}
      style={{
        background: "#fff",
        border: "0.5px solid #DAD8D1",
        borderLeft: "2px solid #2E7D52",
        borderRadius: 14,
        padding: 18,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 500, color: "var(--chalk-board)" }}>
        {featureName} is part of {target}.
      </div>
      {benefit && (
        <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--chalk-muted)", marginTop: 6 }}>
          {benefit}
        </div>
      )}
      <button
        type="button"
        onClick={handleClick}
        data-testid="upgrade-cta"
        style={{
          marginTop: 14, background: "transparent", border: "none",
          color: "#2E7D52", fontWeight: 500, fontSize: 14,
          cursor: "pointer", padding: 0,
          touchAction: "manipulation",
        }}
      >
        {label}
      </button>
      <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 8 }}>
        {open ? "Cancel anytime." : "Paid plans open soon."}
      </div>
    </div>
  );
}

// ── SportBadge ──
const SPORT_STYLE = {
  baseball:  { bg: "var(--sport-baseball-bg)",  fg: "var(--sport-baseball-fg)" },
  softball:  { bg: "var(--sport-baseball-bg)",  fg: "var(--sport-baseball-fg)" },
  soccer:    { bg: "var(--sport-soccer-bg)",    fg: "var(--sport-soccer-fg)" },
  basketball:{ bg: "var(--sport-basket-bg)",    fg: "var(--sport-basket-fg)" },
  football:  { bg: "var(--sport-football-bg)",  fg: "var(--sport-football-fg)" },
  other:     { bg: "var(--sport-default-bg)",   fg: "var(--sport-default-fg)" },
};
export function SportBadge({ sport }) {
  const s = SPORT_STYLE[sport] || SPORT_STYLE.other;
  return (
    <span
      style={{
        background: s.bg, color: s.fg, borderRadius: 999,
        padding: "3px 9px", fontSize: 11, fontWeight: 500, textTransform: "capitalize",
      }}
    >
      {sport}
    </span>
  );
}

// ── ChalkProgressBar ──
export function ChalkProgressBar({ progress }) {
  const pct = Math.max(0, Math.min(1, progress)) * 100;
  return (
    <div style={{ height: 3, background: "rgba(28,43,30,0.10)", borderRadius: 2, overflow: "hidden" }} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div style={{ width: `${pct}%`, height: "100%", background: "var(--chalk-green)", transition: "width 400ms ease" }} />
    </div>
  );
}

// ── RsvpRow ──
export function RsvpRow({ currentStatus, onRsvp, tier = "pickup", testIdPrefix = "rsvp" }) {
  const isChalkboard = tier === "chalkboard" || tier === "clipboard_plus" || tier === "league_os";
  const Btn = ({ status, label, color }) => {
    const active = currentStatus === status;
    return (
      <button
        onClick={() => onRsvp(status)}
        data-testid={`${testIdPrefix}-${status}`}
        aria-label={`Mark as ${label}`}
        style={{
          flex: 1, minHeight: 44,
          background: active ? color : "#fff",
          color: active ? "#fff" : "var(--chalk-board)",
          border: `0.5px solid ${active ? color : "var(--chalk-border)"}`,
          borderRadius: 10, fontWeight: 500, fontSize: 13,
          transition: "all 120ms ease", cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div className="flex gap-2" style={{ marginTop: 12 }}>
      <Btn status="going" label="Going" color="var(--chalk-green)" />
      {isChalkboard && <Btn status="late" label="Late" color="var(--chalk-amber)" />}
      <Btn status="cant_go" label="Can't go" color="#6B6B66" />
    </div>
  );
}

// ── BottomSheet ──
export function BottomSheet({ isOpen, title, onDismiss, children }) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onDismiss && onDismiss();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onDismiss]);
  if (!isOpen) return null;
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 60, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={onDismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderTopLeftRadius: 18, borderTopRightRadius: 18,
          width: "100%", maxWidth: 480, maxHeight: "85vh",
          overflow: "auto", transform: "translateY(0)",
          animation: "chalk-sheet-up 240ms ease",
        }}
      >
        <div style={{ padding: "12px 16px", borderBottom: "0.5px solid var(--chalk-border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 500, fontSize: 15 }}>{title}</div>
          <button data-testid="sheet-dismiss" onClick={onDismiss} aria-label="Close" style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--chalk-muted)" }}>×</button>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
      </div>
      <style>{`@keyframes chalk-sheet-up { from { transform: translateY(40px); opacity: 0.4; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}

// ── BottomNav ──
const COACH_TABS = [
  { id: "board", label: "Board", icon: Home, route: "/app" },
  { id: "playbook", label: "Playbook", icon: ClipboardList, route: "/app/playbook" },
  { id: "roster", label: "Roster", icon: Users, route: "/app/roster" },
  { id: "talk", label: "Talk", icon: Megaphone, route: "/app/talk" },
  { id: "more", label: "More", icon: MoreHorizontal, route: "/app/more" },
];

const PARENT_TABS = [
  { id: "clipboard", label: "Clipboard", icon: Home, route: "/app" },
  { id: "family", label: "Family", icon: Calendar, route: "/app/family" },
  { id: "playbook", label: "Playbook", icon: ClipboardList, route: "/app/playbook" },
  { id: "talk", label: "Talk", icon: Megaphone, route: "/app/talk" },
  { id: "more", label: "More", icon: MoreHorizontal, route: "/app/more" },
];

export function BottomNav({ variant = "coach", activeTab }) {
  const navigate = useNavigate();
  const tabs = variant === "parent" ? PARENT_TABS : COACH_TABS;
  const onTap = (route) => {
    if (navigator && "vibrate" in navigator) navigator.vibrate(8);
    navigate(route);
  };
  return (
    <nav
      data-testid="bottom-nav"
      role="navigation"
      style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "#fff", borderTop: "0.5px solid var(--chalk-border)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        zIndex: 30,
      }}
    >
      <div className="flex" style={{ maxWidth: 480, margin: "0 auto" }}>
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = t.id === activeTab;
          return (
            <button
              key={t.id}
              data-testid={`nav-${t.id}`}
              onClick={() => onTap(t.route)}
              aria-current={isActive ? "page" : undefined}
              aria-label={t.label}
              style={{
                flex: 1, padding: "10px 4px 12px",
                background: "transparent", border: "none",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                cursor: "pointer", color: isActive ? "var(--chalk-board)" : "var(--chalk-muted-2)",
              }}
            >
              {isActive && <span style={{ width: 16, height: 2, background: "var(--chalk-green)", borderRadius: 2, position: "absolute", marginTop: -8 }} />}
              <Icon size={22} aria-hidden="true" />
              <span style={{ fontSize: 11, fontWeight: 500 }}>{t.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ── Chalk lines decoration (used by EmptyState illustrations) ──
export function ChalkIllustrationLines() {
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" aria-hidden="true">
      <rect x="10" y="20" width="100" height="3" rx="1.5" fill="currentColor" opacity="0.5" />
      <rect x="10" y="40" width="80" height="3" rx="1.5" fill="currentColor" opacity="0.4" />
      <rect x="10" y="60" width="65" height="3" rx="1.5" fill="currentColor" opacity="0.3" />
      <rect x="10" y="80" width="45" height="3" rx="1.5" fill="currentColor" opacity="0.2" />
      <rect x="10" y="100" width="30" height="3" rx="1.5" fill="currentColor" opacity="0.15" />
    </svg>
  );
}

export { ChalkLogoLockup };
